import { Link, instructorLinkState, instructorPath, useSharedAvatarStyle } from '@/components/layout/TransitionLink'
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import type { InstructorSummary } from '@/hooks/useAllInstructors'
import { formatPrecise, initials } from '@/lib/format'
import { workoutNouns } from '@/lib/discipline'
import { SpotlightCard } from '@/components/dashboard/fx/SpotlightCard'

interface TopInstructorsCarouselProps {
  instructors: InstructorSummary[]
}

// Instructors can teach across every discipline, so "workouts" rather
// than a discipline-specific noun.
const { plural: WORKOUTS } = workoutNouns('all')

function InstructorCard({ instructor, rank }: { instructor: InstructorSummary; rank: number }) {
  const to = instructorPath(instructor.id)
  const shared = useSharedAvatarStyle(to, instructor.id)
  return (
    <Link to={to} state={instructorLinkState(instructor.name, instructor.image_url)} className="group block">
      <SpotlightCard className="flex w-44 flex-col items-center gap-3 p-5 text-center transition-transform duration-500 group-hover:-translate-y-1.5">
        <div style={shared} className="rounded-full bg-gradient-to-br from-primary via-[#e87ba4] to-[#eb6834] p-[2px]">
          <Avatar className="size-24 border-[3px] border-background">
            <AvatarImage src={instructor.image_url ?? undefined} alt="" />
            <AvatarFallback className="font-display text-xl">{initials(instructor.name)}</AvatarFallback>
          </Avatar>
        </div>
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">
            #{String(rank).padStart(2, '0')}
          </span>
          <span className="truncate font-medium">{instructor.name}</span>
          <span className="text-xs text-muted-foreground">
            {formatPrecise(instructor.ride_count)} {WORKOUTS}
          </span>
        </div>
      </SpotlightCard>
    </Link>
  )
}

// A person's photo is a circle everywhere else in the app -- square
// cover-art tiles are for songs/albums/themes, not people.
export function TopInstructorsCarousel({ instructors }: TopInstructorsCarouselProps) {
  return (
    <Carousel opts={{ align: 'start', dragFree: true }} className="w-full">
      <div className="mb-4 flex items-end justify-between gap-2">
        <div>
          <h2 className="font-display text-2xl font-semibold tracking-tight">Top instructors</h2>
          <p className="text-sm text-muted-foreground">By number of workouts catalogued</p>
        </div>
        <div className="hidden shrink-0 items-center gap-2 sm:flex">
          <CarouselPrevious className="static size-9 translate-x-0 translate-y-0" />
          <CarouselNext className="static size-9 translate-x-0 translate-y-0" />
        </div>
      </div>

      <CarouselContent className="-ml-4">
        {instructors.map((instructor, i) => (
          <CarouselItem key={instructor.id} className="basis-auto pl-4">
            <InstructorCard instructor={instructor} rank={i + 1} />
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  )
}
